import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import {
  Globe, Sparkles, Users, MessageSquare, Palette,
  Check, ArrowLeft, ArrowRight, Dna, Zap,
} from 'lucide-react';
import { colors, spacing, radius, typography, shadows, gradients, transitions } from '../../../styles/theme';
import useResponsive from '../../../utils/useResponsive';
import brandDnaActions from '../../../actions/brandDna';

const toErrMsg = (err) =>
  typeof err?.error === 'string' ? err.error
    : err?.error?.message || err?.message || 'An error occurred';

const PHASES = [
  { key: 'scanning', icon: Globe, label: 'Scanning website', color: colors.brand.primary },
  { key: 'understanding', icon: Sparkles, label: 'Understanding your brand', color: '#a78bfa' },
  { key: 'audience', icon: Users, label: 'Analyzing audience', color: '#f093fb' },
  { key: 'voice', icon: MessageSquare, label: 'Defining voice', color: '#fa8c16' },
  { key: 'visual', icon: Palette, label: 'Capturing visual identity', color: '#52c41a' },
  { key: 'done', icon: Check, label: 'Brand DNA ready', color: colors.status.success },
];

const PROGRESS_RANGES = [
  [0, 15], [15, 40], [40, 60], [60, 80], [80, 95], [95, 100],
];

export default function BrandDnaCreateScreen() {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  // Phase: 'input' | 'extracting' | 'completed' | 'failed'
  const [phase, setPhase] = useState('input');
  const [brandName, setBrandName] = useState('');
  const [brandUrl, setBrandUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [brandId, setBrandId] = useState(null);
  const [extractionId, setExtractionId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const pollRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (nameInputRef.current) nameInputRef.current.focus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Update step based on progress
  useEffect(() => {
    for (let i = PROGRESS_RANGES.length - 1; i >= 0; i--) {
      if (progress >= PROGRESS_RANGES[i][0]) {
        setCurrentStep(i);
        break;
      }
    }
  }, [progress]);

  const handleSubmit = async () => {
    if (!brandName.trim()) {
      message.warning('Give your brand a name');
      return;
    }
    try {
      setCreating(true);
      const res = await brandDnaActions.createBrand({
        brand_name: brandName.trim(),
        source_url: brandUrl.trim() || undefined,
      });

      if (res.success) {
        setBrandId(res.data.brand_id);

        if (brandUrl.trim()) {
          // Start extraction immediately
          setPhase('extracting');
          setProgress(0);
          const extRes = await brandDnaActions.startExtraction(res.data.brand_id, brandUrl.trim());
          if (extRes.success) {
            setExtractionId(extRes.data.extraction_id);
            startPolling(res.data.brand_id, extRes.data.extraction_id);
          } else {
            setPhase('failed');
            setErrorMsg('Failed to start extraction');
          }
        } else {
          // No URL — go straight to detail
          navigate(`/brand-dna/${res.data.brand_id}`);
        }
      }
    } catch (err) {
      if (phase === 'extracting') {
        setPhase('failed');
        setErrorMsg(toErrMsg(err));
      } else {
        message.error(toErrMsg(err));
      }
    } finally {
      setCreating(false);
    }
  };

  const startPolling = useCallback((bId, extId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await brandDnaActions.getExtractionStatus(bId, extId);
        if (res.success) {
          const ext = res.data;
          setProgress(ext.progress_pct);

          if (ext.status === 'completed') {
            clearInterval(pollRef.current);
            setPhase('completed');
            setProgress(100);
          } else if (ext.status === 'failed') {
            clearInterval(pollRef.current);
            setPhase('failed');
            setErrorMsg(ext.error_message || 'Extraction failed');
          }
        }
      } catch {
        // Keep polling on transient errors
      }
    }, 2000);
  }, []);

  const handleRetry = async () => {
    if (!brandId || !brandUrl.trim()) return;
    setPhase('extracting');
    setProgress(0);
    setErrorMsg('');
    try {
      const extRes = await brandDnaActions.startExtraction(brandId, brandUrl.trim());
      if (extRes.success) {
        setExtractionId(extRes.data.extraction_id);
        startPolling(brandId, extRes.data.extraction_id);
      }
    } catch (err) {
      setPhase('failed');
      setErrorMsg(toErrMsg(err));
    }
  };

  // ── Animated background orbs ──
  const BackgroundOrbs = () => (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <motion.div
        animate={{ x: [0, 30, -20, 0], y: [0, -40, 20, 0], scale: [1, 1.1, 0.9, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        style={{
          position: 'absolute', top: '10%', left: '15%', width: 300, height: 300,
          borderRadius: '50%', background: `radial-gradient(circle, ${colors.brand.primary}15 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />
      <motion.div
        animate={{ x: [0, -25, 15, 0], y: [0, 30, -25, 0], scale: [1, 0.9, 1.1, 1] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
        style={{
          position: 'absolute', bottom: '20%', right: '10%', width: 250, height: 250,
          borderRadius: '50%', background: `radial-gradient(circle, ${colors.brand.secondary}12 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />
    </div>
  );

  // ── DNA Helix SVG ──
  const DnaHelix = ({ size = 80, animate = true }) => {
    const pairs = 6;
    const h = size;
    const w = size * 0.6;
    return (
      <motion.svg
        width={w} height={h} viewBox={`0 0 ${w} ${h}`}
        animate={animate ? { rotateY: [0, 360] } : {}}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        style={{ perspective: 600 }}
      >
        {Array.from({ length: pairs }).map((_, i) => {
          const y = (h / (pairs + 1)) * (i + 1);
          const phase = (i / pairs) * Math.PI * 2;
          const x1 = w * 0.5 + Math.sin(phase) * (w * 0.35);
          const x2 = w * 0.5 - Math.sin(phase) * (w * 0.35);
          const opacity = 0.3 + Math.abs(Math.cos(phase)) * 0.7;
          return (
            <g key={i}>
              <line x1={x1} y1={y} x2={x2} y2={y} stroke={colors.brand.primary} strokeWidth={1.5} opacity={opacity * 0.4} />
              <circle cx={x1} cy={y} r={3} fill={colors.brand.primary} opacity={opacity} />
              <circle cx={x2} cy={y} r={3} fill={colors.brand.secondary} opacity={opacity} />
            </g>
          );
        })}
      </motion.svg>
    );
  };

  // ── Progress Ring ──
  const ProgressRing = ({ value, size = 140 }) => {
    const strokeWidth = 4;
    const r = (size - strokeWidth * 2) / 2;
    const circumference = 2 * Math.PI * r;
    const offset = circumference - (value / 100) * circumference;

    return (
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colors.surface.secondary} strokeWidth={strokeWidth} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="url(#progressGradient)" strokeWidth={strokeWidth}
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          strokeDasharray={circumference}
        />
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.brand.primary} />
            <stop offset="100%" stopColor={colors.brand.secondary} />
          </linearGradient>
        </defs>
      </svg>
    );
  };

  const containerPad = isMobile ? spacing.md : spacing.xxxl;

  return (
    <div style={{
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: containerPad,
      position: 'relative',
    }}>
      <BackgroundOrbs />

      {/* Back button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        whileHover={{ x: -3 }}
        onClick={() => navigate('/brand-dna')}
        style={{
          position: 'absolute', top: isMobile ? spacing.md : spacing.xl, left: isMobile ? spacing.md : spacing.xl,
          display: 'flex', alignItems: 'center', gap: spacing.xs,
          background: 'none', border: 'none', color: colors.text.tertiary,
          fontSize: typography.fontSize.md, cursor: 'pointer', zIndex: 10,
        }}
      >
        <ArrowLeft size={18} />
        Back
      </motion.button>

      <AnimatePresence mode="wait">
        {/* ═══════════════════════════════════════════════════════
            INPUT PHASE
        ═══════════════════════════════════════════════════════ */}
        {phase === 'input' && (
          <motion.div
            key="input"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30, scale: 0.95 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            style={{
              width: '100%',
              maxWidth: 520,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* DNA Icon */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              style={{ marginBottom: spacing.xl }}
            >
              <DnaHelix size={100} />
            </motion.div>

            <h1 style={{
              fontSize: isMobile ? typography.fontSize.xxl : typography.fontSize.xxxl,
              fontWeight: typography.fontWeight.bold,
              color: colors.text.primary,
              margin: `0 0 ${spacing.xs}px 0`,
              lineHeight: typography.lineHeight.tight,
            }}>
              Create Brand DNA
            </h1>
            <p style={{
              fontSize: typography.fontSize.lg,
              color: colors.text.tertiary,
              margin: `0 0 ${spacing.xxl}px 0`,
              maxWidth: 380,
              lineHeight: typography.lineHeight.relaxed,
            }}>
              Paste your website and let AI extract your complete brand identity in seconds.
            </p>

            {/* Input Card */}
            <div style={{
              width: '100%',
              padding: spacing.xl,
              borderRadius: radius.xl,
              background: colors.background.secondary,
              border: `1px solid ${colors.border.secondary}`,
              backdropFilter: 'blur(20px)',
              boxShadow: shadows.lg,
            }}>
              {/* Brand Name */}
              <div style={{ marginBottom: spacing.lg, textAlign: 'left' }}>
                <label style={{
                  display: 'block', marginBottom: spacing.xxs,
                  color: colors.text.secondary, fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                }}>
                  Brand Name
                </label>
                <input
                  ref={nameInputRef}
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g. My Company"
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                  style={{
                    width: '100%',
                    padding: `${spacing.sm}px ${spacing.md}px`,
                    background: colors.background.tertiary,
                    border: `1px solid ${colors.border.primary}`,
                    borderRadius: radius.md,
                    color: colors.text.primary,
                    fontSize: typography.fontSize.lg,
                    outline: 'none',
                    transition: transitions.fast,
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.brand.primary}
                  onBlur={(e) => e.target.style.borderColor = colors.border.primary}
                />
              </div>

              {/* Website URL */}
              <div style={{ marginBottom: spacing.xl, textAlign: 'left' }}>
                <label style={{
                  display: 'block', marginBottom: spacing.xxs,
                  color: colors.text.secondary, fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                }}>
                  Website URL
                </label>
                <div style={{ position: 'relative' }}>
                  <Globe size={16} color={colors.text.disabled} style={{
                    position: 'absolute', left: spacing.sm, top: '50%', transform: 'translateY(-50%)',
                  }} />
                  <input
                    value={brandUrl}
                    onChange={(e) => setBrandUrl(e.target.value)}
                    placeholder="https://example.com"
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                    style={{
                      width: '100%',
                      padding: `${spacing.sm}px ${spacing.md}px ${spacing.sm}px 36px`,
                      background: colors.background.tertiary,
                      border: `1px solid ${colors.border.primary}`,
                      borderRadius: radius.md,
                      color: colors.text.primary,
                      fontSize: typography.fontSize.lg,
                      outline: 'none',
                      transition: transitions.fast,
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => e.target.style.borderColor = colors.brand.primary}
                    onBlur={(e) => e.target.style.borderColor = colors.border.primary}
                  />
                </div>
                <p style={{
                  margin: `${spacing.xxs}px 0 0`,
                  fontSize: typography.fontSize.xs,
                  color: colors.text.disabled,
                }}>
                  Optional — we'll analyze your site to extract brand identity, voice, and visual style.
                </p>
              </div>

              {/* Submit Button */}
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: `0 8px 30px ${colors.brand.primary}40` }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSubmit}
                disabled={creating || !brandName.trim()}
                style={{
                  width: '100%',
                  padding: `${spacing.sm + 2}px ${spacing.xl}px`,
                  background: brandUrl.trim() ? gradients.primary : colors.brand.primary,
                  border: 'none',
                  borderRadius: radius.md,
                  color: '#fff',
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.semibold,
                  cursor: creating || !brandName.trim() ? 'not-allowed' : 'pointer',
                  opacity: creating || !brandName.trim() ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: spacing.xs,
                  transition: transitions.normal,
                }}
              >
                {creating ? (
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                    <Zap size={18} />
                  </motion.div>
                ) : brandUrl.trim() ? (
                  <>
                    <Sparkles size={18} />
                    Analyze My Brand
                  </>
                ) : (
                  <>
                    Create Brand
                    <ArrowRight size={18} />
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ═══════════════════════════════════════════════════════
            EXTRACTING PHASE
        ═══════════════════════════════════════════════════════ */}
        {phase === 'extracting' && (
          <motion.div
            key="extracting"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
              maxWidth: 500,
            }}
          >
            {/* Progress Ring with percentage */}
            <div style={{ position: 'relative', marginBottom: spacing.xxl }}>
              <ProgressRing value={progress} size={isMobile ? 120 : 160} />
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              }}>
                <motion.span
                  key={Math.floor(progress)}
                  initial={{ scale: 1.2, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  style={{
                    fontSize: isMobile ? typography.fontSize.xxl : typography.fontSize.xxxl,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.text.primary,
                    lineHeight: 1,
                  }}
                >
                  {Math.round(progress)}%
                </motion.span>
              </div>
            </div>

            <h2 style={{
              fontSize: typography.fontSize.xxl,
              fontWeight: typography.fontWeight.bold,
              color: colors.text.primary,
              margin: `0 0 ${spacing.xs}px 0`,
            }}>
              Analyzing {brandName}
            </h2>
            <p style={{
              fontSize: typography.fontSize.md,
              color: colors.text.tertiary,
              margin: `0 0 ${spacing.xxl}px 0`,
            }}>
              Our AI is discovering your brand's DNA...
            </p>

            {/* Phase Steps */}
            <div style={{
              width: '100%',
              maxWidth: 360,
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.xs,
            }}>
              {PHASES.map((p, i) => {
                const isActive = i === currentStep;
                const isDone = i < currentStep;
                const Icon = p.icon;
                return (
                  <motion.div
                    key={p.key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{
                      opacity: isDone || isActive ? 1 : 0.35,
                      x: 0,
                    }}
                    transition={{ delay: i * 0.08, duration: 0.3 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.sm,
                      padding: `${spacing.xs}px ${spacing.md}px`,
                      borderRadius: radius.md,
                      background: isActive ? `${p.color}12` : 'transparent',
                      border: isActive ? `1px solid ${p.color}30` : '1px solid transparent',
                      transition: transitions.normal,
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: radius.sm,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isDone ? `${colors.status.success}20` : isActive ? `${p.color}20` : colors.surface.primary,
                    }}>
                      {isDone ? (
                        <Check size={14} color={colors.status.success} />
                      ) : isActive ? (
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                          <Icon size={14} color={p.color} />
                        </motion.div>
                      ) : (
                        <Icon size={14} color={colors.text.disabled} />
                      )}
                    </div>
                    <span style={{
                      fontSize: typography.fontSize.md,
                      color: isDone ? colors.status.success : isActive ? colors.text.primary : colors.text.disabled,
                      fontWeight: isActive ? typography.fontWeight.medium : typography.fontWeight.normal,
                    }}>
                      {p.label}
                    </span>
                    {isActive && (
                      <motion.div
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        style={{
                          marginLeft: 'auto',
                          width: 6, height: 6, borderRadius: '50%',
                          background: p.color,
                        }}
                      />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ═══════════════════════════════════════════════════════
            COMPLETED PHASE
        ═══════════════════════════════════════════════════════ */}
        {phase === 'completed' && (
          <motion.div
            key="completed"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
              maxWidth: 480,
            }}
          >
            {/* Success glow */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1] }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              style={{
                width: 100, height: 100, borderRadius: '50%',
                background: `radial-gradient(circle, ${colors.status.success}25 0%, transparent 70%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: spacing.xl,
              }}
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, duration: 0.5, type: 'spring', stiffness: 200 }}
                style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: `${colors.status.success}20`,
                  border: `2px solid ${colors.status.success}60`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Check size={32} color={colors.status.success} strokeWidth={3} />
              </motion.div>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              style={{
                fontSize: typography.fontSize.xxl,
                fontWeight: typography.fontWeight.bold,
                color: colors.text.primary,
                margin: `0 0 ${spacing.xs}px 0`,
              }}
            >
              Brand DNA Extracted
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              style={{
                fontSize: typography.fontSize.lg,
                color: colors.text.tertiary,
                margin: `0 0 ${spacing.xxl}px 0`,
                lineHeight: typography.lineHeight.relaxed,
              }}
            >
              Your brand identity, audience, voice, and visual style have been captured. Review and refine your Brand DNA.
            </motion.p>

            {/* Pillar summary cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: spacing.sm,
                width: '100%',
                marginBottom: spacing.xxl,
              }}
            >
              {[
                { icon: Sparkles, label: 'Core Identity', color: colors.brand.primary },
                { icon: Users, label: 'Audience', color: '#f093fb' },
                { icon: MessageSquare, label: 'Brand Voice', color: '#fa8c16' },
                { icon: Palette, label: 'Visual Style', color: '#52c41a' },
              ].map((pillar, i) => (
                <motion.div
                  key={pillar.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 + i * 0.1 }}
                  style={{
                    padding: spacing.md,
                    borderRadius: radius.lg,
                    background: `${pillar.color}08`,
                    border: `1px solid ${pillar.color}25`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.sm,
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: radius.sm,
                    background: `${pillar.color}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <pillar.icon size={16} color={pillar.color} />
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <span style={{
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.medium,
                      color: colors.text.primary,
                      display: 'block',
                    }}>
                      {pillar.label}
                    </span>
                    <span style={{
                      fontSize: typography.fontSize.xs,
                      color: colors.status.success,
                    }}>
                      Extracted
                    </span>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              whileHover={{ scale: 1.03, boxShadow: `0 8px 30px ${colors.brand.primary}40` }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(`/brand-dna/${brandId}`)}
              style={{
                padding: `${spacing.sm + 2}px ${spacing.xxxl}px`,
                background: gradients.primary,
                border: 'none',
                borderRadius: radius.md,
                color: '#fff',
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.semibold,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: spacing.xs,
              }}
            >
              View Brand DNA
              <ArrowRight size={18} />
            </motion.button>
          </motion.div>
        )}

        {/* ═══════════════════════════════════════════════════════
            FAILED PHASE
        ═══════════════════════════════════════════════════════ */}
        {phase === 'failed' && (
          <motion.div
            key="failed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
              maxWidth: 420,
            }}
          >
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: `${colors.status.error}15`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: spacing.xl,
            }}>
              <Dna size={32} color={colors.status.error} />
            </div>

            <h2 style={{
              fontSize: typography.fontSize.xxl,
              fontWeight: typography.fontWeight.bold,
              color: colors.text.primary,
              margin: `0 0 ${spacing.xs}px 0`,
            }}>
              Extraction Failed
            </h2>
            <p style={{
              fontSize: typography.fontSize.md,
              color: colors.text.tertiary,
              margin: `0 0 ${spacing.lg}px 0`,
              lineHeight: typography.lineHeight.relaxed,
            }}>
              {errorMsg || 'Something went wrong while analyzing your website.'}
            </p>

            <div style={{ display: 'flex', gap: spacing.sm }}>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleRetry}
                style={{
                  padding: `${spacing.sm}px ${spacing.xl}px`,
                  background: gradients.primary,
                  border: 'none',
                  borderRadius: radius.md,
                  color: '#fff',
                  fontSize: typography.fontSize.md,
                  fontWeight: typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                Try Again
              </motion.button>
              {brandId && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(`/brand-dna/${brandId}`)}
                  style={{
                    padding: `${spacing.sm}px ${spacing.xl}px`,
                    background: 'transparent',
                    border: `1px solid ${colors.border.primary}`,
                    borderRadius: radius.md,
                    color: colors.text.secondary,
                    fontSize: typography.fontSize.md,
                    cursor: 'pointer',
                  }}
                >
                  Edit Manually
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
